import { useState, useEffect } from 'react';
import { HelpCircle, ArrowRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { apiRequest } from '../lib/utils';
import AppointmentModal from '../components/AppointmentModal';

const DEFAULT_FAQS = [
  {
    id: '1',
    question: 'What is a Denturist?',
    answer: 'A Denturist is a licensed oral health professional who specializes exclusively in the design, fabrication, fitting, and maintenance of removable dental prosthetics such as full and partial dentures.',
    category: 'General'
  },
  {
    id: '2',
    question: 'Do you perform extractions?',
    answer: 'No. Extractions must be performed by your dentist. We specialize in denture fabrication and fitting. If extractions are required, your dentist performs them, and we coordinate the denture fabrication timing.',
    category: 'Services'
  },
  {
    id: '3',
    question: 'Do you provide implants?',
    answer: 'No. We do not provide implant surgery. We specialize in removable prosthetics including full dentures, partial dentures, and oral appliances.',
    category: 'Services'
  },
  {
    id: '4',
    question: 'Are dentures covered by insurance?',
    answer: 'Many dental insurance plans offer partial coverage for dentures. Contact us with your insurance information and we\'ll help verify your coverage and explain your benefits.',
    category: 'Insurance'
  },
  {
    id: '5',
    question: 'How long does the denture process take?',
    answer: 'Typically several weeks depending on case complexity. Immediate dentures can be provided the same day as extractions (performed by your dentist).',
    category: 'Process'
  },
  {
    id: '6',
    question: 'Do you offer mouth guards?',
    answer: 'Yes. We provide custom-fitted mouth guards for teeth grinding (bruxism), TMJ protection, sports activities, and jaw stabilization.',
    category: 'Services'
  },
  {
    id: '7',
    question: 'Do you offer sleep appliances?',
    answer: 'Yes. We provide custom oral appliances for snoring and mild sleep apnea. Note: Patients must have proper medical evaluation for sleep apnea diagnosis before receiving a sleep appliance.',
    category: 'Services'
  },
  {
    id: '8',
    question: 'How much do dentures cost?',
    answer: 'Costs vary depending on the type of denture and complexity of your case. We provide detailed cost estimates during your consultation with no hidden fees. Our direct care model often results in lower costs than traditional multi-provider paths.',
    category: 'Cost'
  },
  {
    id: '9',
    question: 'How do I care for my dentures?',
    answer: 'Remove and rinse dentures after eating. Brush daily with a soft brush. Soak overnight in denture solution. Handle carefully to avoid dropping. Visit us regularly for professional cleaning and adjustments.',
    category: 'Care'
  },
  {
    id: '10',
    question: 'How often should I have my dentures checked?',
    answer: 'We recommend annual check-ups to ensure proper fit and function. More frequent visits may be needed if you experience discomfort, slipping, or changes in fit.',
    category: 'Care'
  },
];

const CATEGORIES = ['All', 'General', 'Services', 'Insurance', 'Cost', 'Process', 'Care'];

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showAppointment, setShowAppointment] = useState(false);

  useEffect(() => {
    apiRequest('/public/faqs')
      .then((data) => {
        if (data && data.length > 0) {
          setFaqs(data);
        } else {
          setFaqs(DEFAULT_FAQS);
        }
      })
      .catch(() => setFaqs(DEFAULT_FAQS));
  }, []);

  const filteredFaqs = faqs.filter((faq) => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="pt-24 lg:pt-32" data-testid="faq-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4" />
            Questions Answered
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Frequently Asked Questions
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Find answers to common questions about our denture services, insurance, costs, and more.
          </p>

          {/* Search */}
          <div className="relative mt-8 max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 rounded-full bg-white"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`faq-category-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16 lg:py-24 bg-white" data-testid="faq-list">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No questions found matching your search.</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq, idx) => (
                <AccordionItem
                  key={faq.id}
                  value={`item-${faq.id}`}
                  className="bg-slate-50 rounded-xl border-none overflow-hidden"
                >
                  <AccordionTrigger
                    className="px-6 py-5 text-left hover:no-underline"
                    data-testid={`faq-item-${idx}`}
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Still Have Questions?
          </h2>
          <p className="mt-4 text-slate-600">
            Can't find what you're looking for? Contact us and we'll be happy to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button
              onClick={() => setShowAppointment(true)}
              className="bg-orange-600 hover:bg-orange-700 rounded-full px-8"
              data-testid="faq-schedule-btn"
            >
              Schedule Consultation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="border-2 border-slate-900 rounded-full px-8"
              asChild
            >
              <a href="tel:+15415550123">Call (541) 756-2121</a>
            </Button>
          </div>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
